<template>
  <div class="portal-filter-block">
    <!-- Filter Section -->
    <div class="container mb-8 mt-12">
      <div class="mx-auto max-w-[970px] text-center">
        <h2 v-if="title" class="font-headline text-h1">{{ title }}</h2>
        <p v-if="description" class="pt-2 font-body text-body">{{ description }}</p>

        <!-- Category Filters -->
        <div v-if="filterType === 'category' && categories.length" class="mb-6 mt-8">
          <div class="flex flex-wrap justify-center gap-3">
            <app-button
              v-for="category in categories"
              :key="category.id"
              type="radio"
              :variant="theme === 'purple' ? 'lila' : 'rosa'"
              :checked="selectedCategories.includes(category.id)"
              size="desktop"
              @click="toggleCategory(category.id)"
            >
              {{ category.title }}
            </app-button>
          </div>
        </div>

        <!-- Date Filters -->
        <div v-if="filterType === 'date' && availableYears.length" class="mb-6 mt-8">
          <div class="flex flex-wrap justify-center gap-3">
            <app-button
              v-for="year in availableYears"
              :key="year"
              type="radio"
              :variant="theme === 'purple' ? 'lila' : 'rosa'"
              :checked="selectedYears.includes(year)"
              size="desktop"
              @click="toggleYear(year)"
            >
              {{ year }}
            </app-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtered Articles -->
    <div v-if="currentArticles.length || isLoading" class="container">
      <div class="mx-auto">
        <!-- Loading State -->
        <div v-if="isLoading" class="mb-8 py-12 text-center">
          <p class="text-gray-500 text-lg">Laddar artiklar...</p>
        </div>

        <!-- Articles Grid - 3 columns -->
        <div
          v-else-if="currentArticles.length"
          class="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3"
        >
          <content-card
            v-for="article in currentArticles"
            :key="article.sys.id"
            :article="article"
            :link="(article as any)._path || generateArticlePath(article)"
            :contain-image="(article as any)?.fields?.imageOtherAspect === true"
          />
        </div>

        <!-- Load More Button -->
        <div v-if="hasMoreData" class="text-center">
          <app-button
            :variant="theme === 'purple' ? 'ljuslila' : 'ljusrosa'"
            size="desktop"
            type="toggle"
            class="mx-auto mb-6 w-full max-w-md"
            :disabled="isLoadingMore"
            @click="loadMore"
          >
            {{ isLoadingMore ? 'Laddar...' : 'Ladda fler' }}
          </app-button>
        </div>
      </div>
    </div>

    <!-- No Results -->
    <div v-else-if="hasActiveFilters" class="container">
      <div class="mx-auto max-w-4xl py-12 text-center">
        <p class="text-gray-500 text-lg">Inga artiklar hittades för de valda filtren.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Article } from '@/types/contentful'
import { generateArticlePath } from '@/utils/slugResolver'

interface TransformedCategory {
  id: string
  title: string
}

interface PortalFilterBlockProps {
  filterType: 'category' | 'date'
  title?: string
  description?: string
  categories?: TransformedCategory[]
  itemsPerPage?: number
  portalId: string // Required portal ID for fetching articles
  theme?: 'pink' | 'purple' // Theme for button colors
  order?: string // Order parameter for Contentful queries
}

const props = withDefaults(defineProps<PortalFilterBlockProps>(), {
  title: '',
  description: '',
  categories: () => [],
  itemsPerPage: 9,
  theme: 'pink',
  order: '-fields.date',
})

// Get Contentful client and router
const { getArticlesByCategory, getArticlesByParent } = useContentful()
const router = useRouter()
const route = useRoute()

// Helper function to create slug from title
const createSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/å/g, 'a')
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '') // Remove leading/trailing dashes
    .trim()
}

// Helper function to find category by slug
const findCategoryBySlug = (slug: string): TransformedCategory | undefined => {
  return props.categories.find((category) => createSlug(category.title) === slug)
}

// URL state management functions
const updateUrlState = () => {
  const query: Record<string, string> = {}

  if (selectedCategories.value.length > 0) {
    // Convert category IDs to slugs for URL
    const categorySlugs = selectedCategories.value
      .map((id) => {
        const category = props.categories.find((cat) => cat.id === id)
        return category ? createSlug(category.title) : null
      })
      .filter(Boolean)

    if (categorySlugs.length > 0) {
      query.kategori = categorySlugs.join(',')
    }
  }

  if (selectedYears.value.length > 0) {
    query.years = selectedYears.value.join(',')
  }

  if (currentPage.value > 1) {
    query.page = currentPage.value.toString()
  }

  // Update URL without triggering navigation
  router.replace({ query })
}

const parseUrlState = () => {
  const query = route.query

  // Parse categories from URL
  if (query.kategori && typeof query.kategori === 'string') {
    const categorySlugs = query.kategori.split(',').filter(Boolean)
    selectedCategories.value = categorySlugs
      .map((slug) => {
        const category = findCategoryBySlug(slug)
        return category ? category.id : null
      })
      .filter(Boolean) as string[]
  }

  // Parse years from URL
  if (query.years && typeof query.years === 'string') {
    selectedYears.value = query.years
      .split(',')
      .map(Number)
      .filter((year) => !isNaN(year))
  }

  // Parse page from URL
  if (query.page && typeof query.page === 'string') {
    const page = parseInt(query.page, 10)
    if (!isNaN(page) && page > 0) {
      currentPage.value = page
    }
  }
}

// Reactive state
const selectedCategories = ref<string[]>([])
const selectedYears = ref<number[]>([])
const currentPage = ref(1)
const allArticles = ref<Article[]>([])
const filteredArticles = ref<Article[]>([])
const allYears = ref<number[]>([]) // Store all available years for date filtering
const isLoading = ref(false)
const isLoadingMore = ref(false)
const hasMoreData = ref(true)

// Computed properties
const availableYears = computed(() => {
  if (props.filterType !== 'date') return []
  return allYears.value
})

// Function to fetch filtered articles from server with pagination
const fetchFilteredArticles = async (page = 1, append = false) => {
  if (isLoading.value && !append) return

  if (append) {
    isLoadingMore.value = true
  } else {
    isLoading.value = true
    currentPage.value = 1
    filteredArticles.value = []
    hasMoreData.value = true
  }

  try {
    let articles: Article[] = []
    let totalForSelectedYears = 0 // Store total for date filtering
    let totalForCurrentQuery = 0 // Store total for non-date filtering
    let response: any = null // Store response for regular pagination

    if (props.filterType === 'category' && selectedCategories.value.length > 0) {
      // Fetch articles for each selected category with pagination
      const categoryPromises = selectedCategories.value.map(async (categoryId) => {
        const response = await getArticlesByCategory(categoryId, {
          order: props.order,
          include: 2,
          limit: props.itemsPerPage,
          skip: (page - 1) * props.itemsPerPage,
        })
        return response
      })

      const categoryResults = await Promise.all(categoryPromises)

      // Store total for hasMoreData calculation
      totalForCurrentQuery = categoryResults.reduce(
        (sum, response) => sum + (response.total || 0),
        0,
      )

      // Combine all articles and includes from all responses
      articles = categoryResults.flatMap((response) => {
        const items = response.items || []
        // Add includes to each article from its response
        return items.map((article: Article) => ({
          ...article,
          includes: response.includes,
        }))
      })
    } else if (props.filterType === 'date' && selectedYears.value.length > 0) {
      // For date filtering, we'll fetch articles for each selected year using date range queries
      const yearPromises = selectedYears.value.map(async (year) => {
        const startDate = `${year}-01-01T00:00:00.000Z`
        const endDate = `${year + 1}-01-01T00:00:00.000Z`

        const response = await getArticlesByParent(props.portalId, {
          order: props.order,
          include: 2,
          limit: props.itemsPerPage,
          skip: (page - 1) * props.itemsPerPage,
          'fields.date[gte]': startDate,
          'fields.date[lt]': endDate,
        })
        return response
      })

      const yearResults = await Promise.all(yearPromises)

      // Store total for hasMoreData calculation
      totalForSelectedYears = yearResults.reduce((sum, response) => sum + (response.total || 0), 0)

      // Combine all articles and includes from all responses
      articles = yearResults.flatMap((response) => {
        const items = response.items || []
        // Add includes to each article from its response
        return items.map((article: Article) => ({
          ...article,
          includes: response.includes,
        }))
      })
    } else {
      // No filters selected, fetch articles directly
      response = await getArticlesByParent(props.portalId, {
        order: props.order,
        include: 2,
        limit: props.itemsPerPage,
        skip: (page - 1) * props.itemsPerPage,
      })

      articles = (response.items || []).map((article: Article) => ({
        ...article,
        includes: response.includes,
      }))
    }

    // Transform articles to include path
    const newArticles = articles.map((article) => ({
      ...article,
      _path: generateArticlePath(article),
    }))

    if (append) {
      filteredArticles.value = [...filteredArticles.value, ...newArticles]
    } else {
      filteredArticles.value = newArticles
    }

    // Check if there are more articles to load using total from response
    const currentLoaded = page * props.itemsPerPage
    let totalArticles = 0

    if (props.filterType === 'date' && selectedYears.value.length > 0) {
      totalArticles = totalForSelectedYears
    } else if (props.filterType === 'category' && selectedCategories.value.length > 0) {
      totalArticles = totalForCurrentQuery
    } else {
      totalArticles = response?.total || 0
    }

    hasMoreData.value = currentLoaded < totalArticles
  } catch (error) {
    console.error('Error fetching filtered articles:', error)
    if (!append) {
      filteredArticles.value = []
    }
  } finally {
    isLoading.value = false
    isLoadingMore.value = false
  }
}

const hasActiveFilters = computed(() => {
  return selectedCategories.value.length > 0 || selectedYears.value.length > 0
})

const currentArticles = computed(() => {
  return hasActiveFilters.value ? filteredArticles.value : allArticles.value
})

// Methods
const toggleCategory = async (categoryId: string) => {
  const index = selectedCategories.value.indexOf(categoryId)
  if (index > -1) {
    selectedCategories.value.splice(index, 1)
  } else {
    // Limit to 3 categories as mentioned in requirements
    if (selectedCategories.value.length < 3) {
      selectedCategories.value.push(categoryId)
    }
  }

  // Reset pagination and update URL
  currentPage.value = 1
  updateUrlState()
  await fetchFilteredArticles(1, false)
}

const toggleYear = async (year: number) => {
  const index = selectedYears.value.indexOf(year)
  if (index > -1) {
    selectedYears.value.splice(index, 1)
  } else {
    // Limit to 3 years as mentioned in requirements
    if (selectedYears.value.length < 3) {
      selectedYears.value.push(year)
    }
  }

  // Reset pagination and update URL
  currentPage.value = 1
  updateUrlState()
  await fetchFilteredArticles(1, false)
}

const loadMore = async () => {
  currentPage.value++
  const nextPage = currentPage.value

  // Update URL with new page
  updateUrlState()

  if (hasActiveFilters.value) {
    await fetchFilteredArticles(nextPage, true)
  } else {
    await fetchAllArticles(nextPage, true)
  }
}

// Function to fetch articles for the portal with pagination
const fetchAllArticles = async (page = 1, append = false) => {
  if (append) {
    isLoadingMore.value = true
  } else {
    isLoading.value = true
    currentPage.value = 1
    allArticles.value = []
    hasMoreData.value = true
  }

  try {
    const response = await getArticlesByParent(props.portalId, {
      order: props.order,
      include: 2,
      limit: props.itemsPerPage,
      skip: (page - 1) * props.itemsPerPage,
    })

    const newArticles = (response.items || []).map((article: Article) => ({
      ...article,
      _path: generateArticlePath(article),
      includes: response.includes,
    }))

    if (append) {
      allArticles.value = [...allArticles.value, ...newArticles]
    } else {
      allArticles.value = newArticles
    }

    // Check if there are more articles to load using total from response
    const currentLoaded = page * props.itemsPerPage
    hasMoreData.value = currentLoaded < (response.total || 0)
  } catch (error) {
    console.error('Error fetching articles:', error)
    if (!append) {
      allArticles.value = []
    }
  } finally {
    isLoading.value = false
    isLoadingMore.value = false
  }
}

// Function to fetch all years for date filtering (only for klubbnytt)
const fetchAllYears = async () => {
  try {
    // Fetch all articles to get all available years
    // Use a large limit to get all years, but don't include full content
    const response = await getArticlesByParent(props.portalId, {
      order: props.order,
      include: 0, // Don't include linked entries to reduce payload
      limit: 100, // Fetch a large number to get all years
      select: 'fields.date', // Only select the date field to minimize data transfer
    })

    const years = new Set<number>()
    response.items?.forEach((article: Article) => {
      if (article.fields?.date) {
        const year = new Date(article.fields.date).getFullYear()
        years.add(year)
      }
    })

    allYears.value = Array.from(years).sort((a, b) => b - a) // Sort descending (newest first)
  } catch (error) {
    console.error('Error fetching years:', error)
    allYears.value = []
  }
}

// Function to load all pages up to a specific page number
const loadAllPagesUpTo = async (targetPage: number) => {
  if (targetPage <= 1) return

  // Load pages 1 through targetPage
  for (let page = 1; page <= targetPage; page++) {
    if (hasActiveFilters.value) {
      await fetchFilteredArticles(page, page > 1) // Append for pages > 1
    } else {
      await fetchAllArticles(page, page > 1) // Append for pages > 1
    }
  }
}

// Watch for route changes and re-parse URL state
watch(
  () => route.query,
  () => {
    parseUrlState()

    // Re-fetch articles when URL changes
    if (hasActiveFilters.value) {
      fetchFilteredArticles(1, false)
    } else {
      fetchAllArticles(1, false)
    }
  },
  { deep: true },
)

// Fetch articles when component mounts
onMounted(async () => {
  // Parse URL state first
  parseUrlState()

  // Only fetch years for date filtering (klubbnytt)
  if (props.filterType === 'date') {
    await fetchAllYears()
  }

  // If URL has a page > 1, load all pages up to that page
  if (currentPage.value > 1) {
    await loadAllPagesUpTo(currentPage.value)
  } else {
    // If we have active filters from URL, fetch filtered articles
    // Otherwise fetch all articles
    if (hasActiveFilters.value) {
      await fetchFilteredArticles(1, false)
    } else {
      await fetchAllArticles(1, false)
    }
  }
})
</script>
